// src/pages/ExperienceLetter.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import '../letterstyles/ExperienceLetter.css';

const ExperienceLetter = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();
  const user = state?.user;

  const superAdminId = Number(localStorage.getItem('adminuserId')) || 0;

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Add date management states
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Temporary states for modal inputs
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);

  const handlePrint = useReactToPrint({
      contentRef: printRef,
      documentTitle: 'Experience_Letter',
   });

  const handleDownloadPDF = () => {
    const element = printRef.current;
    const opt = {
      margin: [5, 5, 5, 5],
      filename: "Experience_Letter.pdf",
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        dpi: 300,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compressPdf: true,
      },
    };
    html2pdf().set(opt).from(element).save();
  };

  // Helper function to format input dates cleanly (e.g., 01-Jan-2021)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-');
  };

  const handleSaveDates = (e) => {
    e.preventDefault();
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!user) {
      alert("No user data found!");
      navigate('/');
    }

    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, '')}`;
          }
          setCompany(data);
        } else {
          console.error('Failed to fetch company data');
        }
      } catch (err) {
        console.error('Error fetching company details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [navigate, user, superAdminId]);

  if (!user) return null;
  if (loading) return <p>Loading company details...</p>;

  return (
    <div className="experience-wrapper">
      <div className="letter-actions">
        {/* Kept your exact buttons and appended the date setup tool directly alongside them */}
        <button className="letter-action-button" onClick={() => setIsModalOpen(true)}>Select Dates</button>
        <button className="letter-action-button" onClick={handlePrint}>Print</button>
        <button className="letter-action-button" onClick={handleDownloadPDF}>Download</button>
      </div>
      <div className="experience-letter" ref={printRef}>
        <div className="letter-header">
          <img 
            src={company?.logoUrl || '/default-logo.png'} 
            alt="Company Logo" 
            className="letter-logo" 
          />
          <div className="letter-company-info" style={{ textAlign: 'center' }}>
            <h2>{company?.name}</h2>
            <p><strong>CIN:</strong> {company?.cin}</p>
            <p><strong>Regt. Office:</strong> {company?.address}</p>
            <p><strong>Email:</strong> {company?.email}</p>
            <p><strong>Contact:</strong> {company?.contact || 'N/A'}</p>
          </div>
          <img
            style={{ opacity: 0 }}
            src={company?.logoUrl || "/default-logo.png"}
            alt="Company Logo"
            className="letter-logo"
          />
        </div>

        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <h2 style={{ margin: 0, fontWeight: "600", fontSize: "20px", letterSpacing: "1px", textTransform: "uppercase" }}>
            Experience Letter
          </h2>
        </div>

        <div className="letter-body">
          <p style={{ textAlign: 'right' }}><strong>Date:</strong> {new Date().toLocaleDateString("en-GB")}</p>
          <p><strong>To,</strong></p>
          <p>{user.fullName || user.name}</p>
          <p>{user.branch}</p>

          <p><strong>Subject:</strong> Experience Certificate</p>
          <p><strong>Designation:</strong> {user?.roleName || user?.role}</p>

          <p>Dear {user.fullName || user.name},</p>

          <p>
            This is to certify that <strong>{user.fullName || user.name}</strong> was employed with <strong>{company?.name}</strong> as a <strong>{user.roleName || user.role}</strong> at our <strong>{user.branch}</strong> branch from <strong>{fromDate?formatDate(fromDate):"N/A"}</strong> to <strong>{toDate? formatDate(toDate):"N/A"}</strong>.
          </p>

          <p>
            During the tenure, {user.fullName || user.name} exhibited excellent performance, professionalism, and dedication to responsibilities.
            We appreciate the contributions and wish the best in future endeavors.
          </p>

          <p>We wish {user.fullName || user.name} all the success in future.</p>

          <p>Regards,</p>
          <br />
          <p><strong>HR Manager</strong><br /><br />{company?.name}</p>
        </div>

        <div className="letter-footer">
          <hr />
          <p>{company?.name} | CIN: {company?.cin}</p>
          <p>{company?.email} | {company?.contact}</p>
        </div>
      </div>

      

      {/* Date Configuration Overlay Form */}
      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Select Certificate Duration</h3>
            <form onSubmit={handleSaveDates}>
              <div className="form-group">
                <label>From Date:</label>
                <input 
                  type="date" 
                  value={tempFromDate} 
                  onChange={(e) => setTempFromDate(e.target.value)} 
                  required
                />
              </div>
              <div className="form-group">
                <label>To Date:</label>
                <input 
                  type="date" 
                  value={tempToDate} 
                  onChange={(e) => setTempToDate(e.target.value)} 
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save">Apply Dates</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceLetter;